/**
 * Location group roles admin API module — a user's location role across a
 * location group: once (the group's locations today) or following the group
 * (locations that join later get it, locations that leave lose it).
 *
 * Endpoints (under /accounts/{accountId}):
 *   POST   /location-group-roles/preview              → plan; writes nothing (omit user_id for an invitation)
 *   GET    /users/{uid}/location-group-roles          → { follows: [...] }
 *   PUT    /users/{uid}/location-group-roles/{gid}    → plan; { role_id, follow }
 *                                                       403 GROUP_FOLLOW_FORBIDDEN when the caller can't follow
 *   DELETE /users/{uid}/location-group-roles/{gid}    → 204; roles stay; 404 when not following
 *
 * Every location is authorized like a single assignment; a location the
 * caller can't change comes back as `skipped`. Setting the user's role at
 * one of the group's locations (POST /roles/{role}/users/{user}) ends the
 * follow on the server.
 */
import { webApiClient } from '../apiClient';
import { toLocationGroupError, LocationGroupError } from './locationGroups';
import { roleAssignErrorText } from '../roleAssignErrors';

export type GroupRoleChange = 'added' | 'changed' | 'unchanged' | 'removed' | 'none' | 'skipped';

export interface GroupRoleLocationChange {
  location_id: string;
  location_name: string;
  current_role_id?: string | null;
  current_role_name?: string;
  change: GroupRoleChange;
  reason?: string;
  /** Why skipped: 'not_allowed' (can't give roles at this store), 'not_held' / 'staff_only' (the role's permissions). */
  reason_code?: 'not_allowed' | 'not_held' | 'staff_only';
}

export interface GroupRolePlan {
  location_group_id: string;
  location_group_name: string;
  role_id: string | null;
  role_name?: string;
  follow: boolean;
  can_follow: boolean;
  following: boolean;
  /** Whether the chosen role can be given at one or more of the group's stores (always true for No access). Absent on an older API. */
  role_assignable?: boolean;
  /** Why not, when no store can take it. */
  role_not_assignable_reason?: 'not_held' | 'staff_only' | null;
  locations: GroupRoleLocationChange[];
  summary: { added: number; changed: number; unchanged: number; removed: number; skipped: number };
}

export interface GroupRoleFollow {
  id: string;
  user_id: string;
  role_id: string;
  role_name?: string;
  location_group_id: string;
  location_group_name?: string;
  account_id: string;
}

const MESSAGES: Record<string, string> = {
  GROUP_FOLLOW_FORBIDDEN: 'Only someone who can give this role at every location of the account can make it follow a group.',
};

function toError(res: { error?: string; errorBody?: any }, fallback: string): LocationGroupError {
  const code: string | undefined = res.errorBody?.code;
  if (code && MESSAGES[code]) return new LocationGroupError(MESSAGES[code], code);
  if (code === 'ROLE_NOT_ASSIGNABLE' || code === 'INSUFFICIENT_PERMISSIONS') {
    return new LocationGroupError(roleAssignErrorText({ status: 403, body: res.errorBody, message: res.error }), code);
  }
  return toLocationGroupError(res, fallback);
}

export async function previewGroupRole(
  accountId: string,
  body: { user_id?: string; location_group_id: string; role_id: string | null; follow: boolean }
): Promise<GroupRolePlan> {
  const res = await webApiClient.post<GroupRolePlan>(`/accounts/${accountId}/location-group-roles/preview`, body);
  if (!res.success) throw toError(res, 'Failed to work out the group change');
  return res.data as GroupRolePlan;
}

export async function applyGroupRole(
  accountId: string,
  userId: string,
  groupId: string,
  body: { role_id: string | null; follow: boolean }
): Promise<GroupRolePlan> {
  const res = await webApiClient.put<GroupRolePlan>(`/accounts/${accountId}/users/${userId}/location-group-roles/${groupId}`, body);
  if (!res.success) throw toError(res, 'Failed to give the role across the group');
  return res.data as GroupRolePlan;
}

/** Ends the follow; the roles stay. A follow that is already gone is not an error. */
export async function stopFollowingGroup(accountId: string, userId: string, groupId: string): Promise<void> {
  const res = await webApiClient.delete(`/accounts/${accountId}/users/${userId}/location-group-roles/${groupId}`);
  if (!res.success && res.status !== 404 && res.errorBody?.code !== 'LOCATION_GROUP_NOT_FOUND') {
    throw toError(res, 'Failed to stop following the group');
  }
}

export async function getGroupRoleFollows(accountId: string, userId: string): Promise<GroupRoleFollow[]> {
  const res = await webApiClient.get<{ follows: GroupRoleFollow[] }>(`/accounts/${accountId}/users/${userId}/location-group-roles`);
  if (!res.success) throw toError(res, 'Failed to load the user’s group roles');
  return Array.isArray(res.data?.follows) ? res.data.follows : [];
}

export const locationGroupRolesAdmin = {
  previewGroupRole,
  applyGroupRole,
  stopFollowingGroup,
  getGroupRoleFollows,
};
